import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

function CodeBlock({ title, language, code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-gray-900 rounded-xl overflow-hidden" data-testid={`code-block-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
        <span className="text-xs text-gray-400">{title} — {language}</span>
        <button onClick={handleCopy} className="text-gray-400 hover:text-white transition-colors" data-testid={`copy-btn-${title.toLowerCase().replace(/\s+/g, '-')}`}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="p-4 text-sm text-gray-300 overflow-x-auto"><code>{code}</code></pre>
    </div>
  );
}

export default function PlatformGetStarted() {
  const firstKey = localStorage.getItem('platform_first_api_key') || 'pbp_test_sk_your_key_here';
  const baseUrl = 'https://www.pebbles.financial/api/platform/v1';

  return (
    <div className="max-w-3xl" data-testid="platform-get-started-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Get Started
        </h1>
        <p className="text-gray-500 mt-1">Create your first wallet in under 5 minutes</p>
      </div>

      <div className="bg-pink-50 border border-pink-200 rounded-xl p-5 mb-8" data-testid="api-key-display">
        <p className="text-sm font-semibold text-gray-900 mb-2">Your Test API Key</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-white px-4 py-2.5 rounded-lg text-sm font-mono text-gray-800 border border-pink-200 truncate" data-testid="test-api-key-value">
            {firstKey}
          </code>
          <button
            onClick={() => { navigator.clipboard.writeText(firstKey); toast.success('Copied!'); }}
            data-testid="copy-api-key-btn"
            className="px-3 py-2.5 bg-[#E8678B] text-white rounded-lg hover:bg-[#d15a7d] transition-colors"
          >
            <Copy size={16} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">This key only works with sandbox (test) data. Go live to get a production key.</p>
      </div>

      <div className="space-y-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-full bg-[#E8678B] text-white flex items-center justify-center text-sm font-bold">1</div>
            <h3 className="text-lg font-semibold text-gray-900">Create a wallet</h3>
          </div>
          <p className="text-sm text-gray-600 mb-3 ml-10">Each of your users gets a wallet with a virtual bank account.</p>
          <CodeBlock
            title="Create wallet"
            language="cURL"
            code={`curl -X POST ${baseUrl}/wallets \\
  -H "X-API-Key: ${firstKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user_001",
    "name": "John Doe",
    "email": "john@example.com"
  }'`}
          />
        </div>

        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-full bg-[#E8678B] text-white flex items-center justify-center text-sm font-bold">2</div>
            <h3 className="text-lg font-semibold text-gray-900">Simulate a deposit (sandbox)</h3>
          </div>
          <p className="text-sm text-gray-600 mb-3 ml-10">In sandbox mode, simulate money arriving in a wallet.</p>
          <CodeBlock
            title="Simulate deposit"
            language="cURL"
            code={`curl -X POST ${baseUrl}/sandbox/simulate-deposit \\
  -H "X-API-Key: ${firstKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "wallet_id": "WALLET_ID_FROM_STEP_1",
    "amount": "10000"
  }'`}
          />
        </div>

        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-full bg-[#E8678B] text-white flex items-center justify-center text-sm font-bold">3</div>
            <h3 className="text-lg font-semibold text-gray-900">Send a transfer</h3>
          </div>
          <p className="text-sm text-gray-600 mb-3 ml-10">Transfer money from a wallet to any Nigerian bank account.</p>
          <CodeBlock
            title="Bank transfer"
            language="cURL"
            code={`curl -X POST ${baseUrl}/transfers \\
  -H "X-API-Key: ${firstKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "wallet_id": "WALLET_ID",
    "amount": "5000",
    "bank_code": "044",
    "account_number": "0123456789",
    "account_name": "Jane Doe"
  }'`}
          />
        </div>

        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-full bg-[#E8678B] text-white flex items-center justify-center text-sm font-bold">4</div>
            <h3 className="text-lg font-semibold text-gray-900">Check wallet balance</h3>
          </div>
          <CodeBlock
            title="Get wallet"
            language="cURL"
            code={`curl ${baseUrl}/wallets/WALLET_ID \\
  -H "X-API-Key: ${firstKey}"`}
          />
        </div>
      </div>

      <div className="mt-10 p-6 bg-gray-50 border border-gray-200 rounded-xl">
        <h3 className="text-base font-semibold text-gray-900 mb-3">Ready for production?</h3>
        <p className="text-sm text-gray-600 mb-3">Complete your verification to get live API keys with real banking.</p>
        <a href="/platform/go-live" className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d15a7d] transition-colors" data-testid="start-verification-btn">
          Start verification &rarr;
        </a>
      </div>
    </div>
  );
}
